<template>
  <div class="offer-page bg-slate-50">
    <!-- Hero Section -->
    <section class="relative overflow-hidden pt-32 pb-20 bg-white">
      <!-- Subtle Background -->
      <div class="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-br from-blue-100 to-purple-100 rounded-full blur-3xl opacity-60 -translate-y-1/2 translate-x-1/3"></div>
      <div class="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-br from-emerald-100 to-cyan-100 rounded-full blur-3xl opacity-40 translate-y-1/2 -translate-x-1/3"></div>
      
      <div class="container mx-auto px-4 relative z-10">
        <div class="grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
          <!-- Left Column: Content -->
          <div class="max-w-xl">
            <!-- Badge -->
            <div class="inline-flex items-center rounded-full bg-blue-50 border border-blue-200 text-blue-700 px-4 py-2 text-sm font-semibold mb-6">
              <span class="w-2 h-2 rounded-full bg-blue-500 mr-2 animate-pulse"></span>
              {{ t('offertaPMI.hero.badge') }}
            </div>
            
            <!-- Main Headline -->
            <h1 class="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-4">
              {{ t('offertaPMI.hero.title') }}
              <span class="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-emerald-500">{{ t('offertaPMI.hero.titleHighlight') }}</span>
            </h1>
            
            <!-- Subheadline -->
            <p class="text-xl sm:text-2xl text-gray-700 font-semibold leading-relaxed mb-8">
              {{ t('offertaPMI.hero.subtitle') }} <span class="text-emerald-600">{{ t('offertaPMI.hero.subtitlePercent') }}</span> {{ t('offertaPMI.hero.subtitleEnd') }}
            </p>
            
            <!-- CTAs -->
            <div class="flex flex-col sm:flex-row gap-4 mb-8">
              <router-link 
                to="/contact" 
                class="inline-flex items-center justify-center h-14 px-8 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-xl font-semibold text-lg transition-all duration-300 shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 hover:-translate-y-0.5"
              >
                {{ t('offertaPMI.hero.cta') }}
                <ArrowRight class="ml-2 h-5 w-5" />
              </router-link>
              
              <button 
                @click="scrollToHow"
                class="inline-flex items-center justify-center h-14 px-8 bg-white border-2 border-gray-200 hover:border-blue-300 text-gray-700 hover:text-blue-600 rounded-xl font-semibold text-lg transition-all duration-300"
              >
                <Play class="mr-2 h-5 w-5" />
                {{ t('offertaPMI.hero.ctaSecondary') }}
              </button>
            </div>
            
            <!-- Trust Indicators -->
            <div class="flex items-center gap-6 text-sm text-gray-500">
              <div class="flex items-center">
                <Check class="h-5 w-5 text-emerald-500 mr-2" />
                {{ t('offertaPMI.hero.trust1') }}
              </div>
              <div class="flex items-center">
                <Check class="h-5 w-5 text-emerald-500 mr-2" />
                {{ t('offertaPMI.hero.trust2') }}
              </div>
            </div>
          </div>
          
          <!-- Right Column: Visual -->
          <div class="relative hidden lg:block">
            <!-- Main Dashboard Card -->
            <div class="relative bg-white rounded-2xl shadow-2xl shadow-gray-200/50 p-6 border border-gray-100">
              <!-- Dashboard Header -->
              <div class="flex items-center justify-between mb-6">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                    <LayoutDashboard class="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 class="font-semibold text-gray-900">{{ t('offertaPMI.dashboard.title') }}</h3>
                    <p class="text-sm text-gray-500">{{ t('offertaPMI.dashboard.subtitle') }}</p>
                  </div>
                </div>
                <div class="flex gap-2">
                  <div class="w-3 h-3 rounded-full bg-red-400"></div>
                  <div class="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div class="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
              </div>
              
              <!-- Stats Grid -->
              <div class="grid grid-cols-3 gap-4 mb-6">
                <div class="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl p-4 text-center">
                  <div class="text-2xl font-bold text-blue-600">{{ t('offertaPMI.dashboard.stat1Value') }}</div>
                  <div class="text-xs text-gray-600">{{ t('offertaPMI.dashboard.stat1Label') }}</div>
                </div>
                <div class="bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-xl p-4 text-center">
                  <div class="text-2xl font-bold text-emerald-600">{{ t('offertaPMI.dashboard.stat2Value') }}</div>
                  <div class="text-xs text-gray-600">{{ t('offertaPMI.dashboard.stat2Label') }}</div>
                </div>
                <div class="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-xl p-4 text-center">
                  <div class="text-2xl font-bold text-purple-600">{{ t('offertaPMI.dashboard.stat3Value') }}</div>
                  <div class="text-xs text-gray-600">{{ t('offertaPMI.dashboard.stat3Label') }}</div>
                </div>
              </div>
              
              <!-- Activity -->
              <div class="space-y-3">
                <div class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div class="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                    <Check class="h-4 w-4 text-emerald-600" />
                  </div>
                  <div class="flex-1">
                    <p class="text-sm font-medium text-gray-900">{{ t('offertaPMI.dashboard.activity1Title') }}</p>
                    <p class="text-xs text-gray-500">{{ t('offertaPMI.dashboard.activity1Desc') }}</p>
                  </div>
                  <span class="text-xs text-gray-400">{{ t('offertaPMI.dashboard.activity1Time') }}</span>
                </div>
                <div class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <Mail class="h-4 w-4 text-blue-600" />
                  </div>
                  <div class="flex-1">
                    <p class="text-sm font-medium text-gray-900">{{ t('offertaPMI.dashboard.activity2Title') }}</p>
                    <p class="text-xs text-gray-500">{{ t('offertaPMI.dashboard.activity2Desc') }}</p>
                  </div>
                  <span class="text-xs text-gray-400">{{ t('offertaPMI.dashboard.activity2Time') }}</span>
                </div>
              </div>
            </div>
            
            <!-- Floating Card -->
            <div class="absolute -left-8 bottom-12 bg-white rounded-xl shadow-xl shadow-gray-200/50 p-4 border border-gray-100 w-56">
              <div class="flex items-center gap-3">
                <div class="w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-500 rounded-xl flex items-center justify-center">
                  <TrendingUp class="h-6 w-6 text-white" />
                </div>
                <div>
                  <div class="text-xs text-gray-500">{{ t('offertaPMI.dashboard.monthLabel') }}</div>
                  <div class="text-xl font-bold text-gray-900">{{ t('offertaPMI.dashboard.monthValue') }}</div>
                  <div class="text-xs text-emerald-600 font-medium">{{ t('offertaPMI.dashboard.monthGrowth') }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Calculator Section -->
    <section class="py-16 bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 relative overflow-hidden">
      <!-- Background decoration -->
      <div class="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
      <div class="absolute bottom-0 left-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>
      
      <div class="container mx-auto px-4 relative z-10">
        <div class="max-w-4xl mx-auto">
          <div class="grid lg:grid-cols-2 gap-12 items-center">
            <!-- Left: Calculator -->
            <div class="bg-white rounded-2xl p-6 md:p-8 shadow-2xl">
              <div class="flex items-center gap-3 mb-6">
                <div class="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center">
                  <Calculator class="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 class="text-xl font-bold text-gray-900">{{ t('offertaPMI.calculator.title') }}</h3>
                  <p class="text-sm text-gray-500">{{ t('offertaPMI.calculator.subtitle') }}</p>
                </div>
              </div>
              
              <!-- Input Fields -->
              <div class="space-y-4 mb-6">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    {{ t('offertaPMI.calculator.clientsLabel') }}
                  </label>
                  <input 
                    v-model.number="calc.clients" 
                    type="number" 
                    :placeholder="t('offertaPMI.calculator.clientsPlaceholder')"
                    class="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all text-lg"
                  />
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    {{ t('offertaPMI.calculator.hoursLabel') }}
                  </label>
                  <input 
                    v-model.number="calc.hours" 
                    type="number" 
                    :placeholder="t('offertaPMI.calculator.hoursPlaceholder')"
                    class="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all text-lg"
                  />
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    {{ t('offertaPMI.calculator.valueLabel') }}
                  </label>
                  <input 
                    v-model.number="calc.value" 
                    type="number" 
                    :placeholder="t('offertaPMI.calculator.valuePlaceholder')"
                    class="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all text-lg"
                  />
                </div>
              </div>
              
              <!-- Calculate Button -->
              <button 
                @click="calculateLoss"
                class="w-full h-14 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-xl font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              >
                <Zap class="h-5 w-5" />
                {{ t('offertaPMI.calculator.calculate') }}
              </button>
            </div>
            
            <!-- Right: Result or Intro -->
            <div class="text-white">
              <template v-if="!calcResult.show">
                <h2 class="text-3xl md:text-4xl font-bold mb-4">
                  {{ t('offertaPMI.calculator.introTitle') }}
                </h2>
                <p class="text-xl text-blue-100 mb-6">
                  {{ t('offertaPMI.calculator.introDesc') }}
                </p>
                <div class="space-y-3">
                  <div class="flex items-center gap-3 text-blue-100">
                    <Check class="h-5 w-5 text-emerald-400" />
                    <span>{{ t('offertaPMI.calculator.introPoint1') }}</span>
                  </div>
                  <div class="flex items-center gap-3 text-blue-100">
                    <Check class="h-5 w-5 text-emerald-400" />
                    <span>{{ t('offertaPMI.calculator.introPoint2') }}</span>
                  </div>
                  <div class="flex items-center gap-3 text-blue-100">
                    <Check class="h-5 w-5 text-emerald-400" />
                    <span>{{ t('offertaPMI.calculator.introPoint3') }}</span>
                  </div>
                </div>
              </template>
              
              <!-- Result Display -->
              <template v-else>
                <div class="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 mb-6">
                  <p class="text-blue-200 text-sm font-medium mb-2">{{ t('offertaPMI.calculator.resultLabel') }}</p>
                  <div class="text-5xl md:text-6xl font-black text-white mb-2">
                    CHF {{ calcResult.monthlyLoss.toLocaleString('de-CH') }}
                  </div>
                  <p class="text-blue-200">{{ t('offertaPMI.calculator.resultSuffix') }}</p>
                </div>
                
                <div class="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 mb-6">
                  <div class="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <div class="text-3xl font-bold text-white">{{ calcResult.hoursLost }}h</div>
                      <p class="text-sm text-blue-200">{{ t('offertaPMI.calculator.hoursLostLabel') }}</p>
                    </div>
                    <div>
                      <div class="text-3xl font-bold text-emerald-400">CHF {{ calcResult.yearlyLoss.toLocaleString('de-CH') }}</div>
                      <p class="text-sm text-blue-200">{{ t('offertaPMI.calculator.yearlyLossLabel') }}</p>
                    </div>
                  </div>
                </div>
                
                <!-- Advice -->
                <div class="bg-emerald-500/20 border border-emerald-400/30 rounded-xl p-4 mb-6">
                  <div class="flex items-start gap-3">
                    <Lightbulb class="h-6 w-6 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <p class="text-white text-sm leading-relaxed">
                      <span class="font-bold">{{ t('offertaPMI.calculator.adviceStart') }}</span> {{ t('offertaPMI.calculator.adviceMiddle') }}
                      <span class="text-emerald-400 font-bold">CHF {{ Math.round(calcResult.monthlyLoss * 0.5).toLocaleString('de-CH') }}/{{ t('offertaPMI.dashboard.monthLabel').toLowerCase() }}</span>, 
                      {{ t('offertaPMI.calculator.adviceEnd') }}
                    </p>
                  </div>
                </div>
                
                <!-- CTA -->
                <router-link 
                  to="/contact" 
                  class="inline-flex items-center justify-center w-full h-14 bg-white text-blue-600 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                >
                  {{ t('offertaPMI.calculator.resultCta') }}
                  <ArrowRight class="ml-2 h-5 w-5" />
                </router-link>
              </template>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Problem Section -->
    <section class="py-20 bg-slate-50">
      <div class="container mx-auto px-4">
        <div class="max-w-4xl mx-auto">
          <!-- Section Header -->
          <div class="text-center mb-12">
            <div class="inline-flex items-center rounded-full bg-red-50 border border-red-200 text-red-700 px-4 py-2 text-sm font-semibold mb-4">
              {{ t('offertaPMI.problem.badge') }}
            </div>
            <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {{ t('offertaPMI.problem.title') }}
            </h2>
            <p class="text-lg text-gray-600 max-w-2xl mx-auto">
              {{ t('offertaPMI.problem.intro') }} <span class="font-semibold text-gray-900">{{ t('offertaPMI.problem.introHighlight1') }}</span> {{ t('offertaPMI.problem.introMiddle') }} <span class="font-semibold text-emerald-600">{{ t('offertaPMI.problem.introHighlight2') }}</span>{{ t('offertaPMI.problem.introEnd') }}
            </p>
          </div>
          
          <!-- Problem Cards -->
          <div class="grid md:grid-cols-3 gap-6">
            <div class="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <div class="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4">
                <AlertCircle class="h-6 w-6 text-red-600" />
              </div>
              <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ t('offertaPMI.problem.problem1Title') }}</h3>
              <p class="text-gray-600 text-sm">{{ t('offertaPMI.problem.problem1Desc') }}</p>
            </div>
            
            <div class="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <div class="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                <Mail class="h-6 w-6 text-orange-600" />
              </div>
              <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ t('offertaPMI.problem.problem2Title') }}</h3>
              <p class="text-gray-600 text-sm">{{ t('offertaPMI.problem.problem2Desc') }}</p>
            </div>
            
            <div class="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <div class="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                <UserX class="h-6 w-6 text-amber-600" />
              </div>
              <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ t('offertaPMI.problem.problem3Title') }}</h3>
              <p class="text-gray-600 text-sm">{{ t('offertaPMI.problem.problem3Desc') }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Solution Section -->
    <section id="how-it-works" class="py-20 bg-white">
      <div class="container mx-auto px-4">
        <div class="max-w-6xl mx-auto">
          <!-- Section Header -->
          <div class="text-center mb-16">
            <div class="inline-flex items-center rounded-full bg-blue-50 border border-blue-200 text-blue-700 px-4 py-2 text-sm font-semibold mb-4">
              {{ t('offertaPMI.solution.badge') }}
            </div>
            <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {{ t('offertaPMI.solution.title') }} <span class="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">{{ t('offertaPMI.solution.titleHighlight') }}</span> {{ t('offertaPMI.solution.titleEnd') }}
            </h2>
            <p class="text-lg text-gray-600 max-w-2xl mx-auto">
              {{ t('offertaPMI.solution.subtitle') }}
            </p>
          </div>
          
          <!-- Solution Grid -->
          <div class="grid md:grid-cols-2 gap-8">
            <!-- Card 1 -->
            <div class="group bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 border border-blue-100 hover:shadow-lg hover:shadow-blue-100/50 transition-all duration-300">
              <div class="flex items-start gap-5">
                <div class="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-500/25">
                  <Search class="h-7 w-7 text-white" />
                </div>
                <div>
                  <h3 class="text-xl font-bold text-gray-900 mb-2">{{ t('offertaPMI.solution.benefit1Title') }}</h3>
                  <p class="text-gray-600 leading-relaxed">{{ t('offertaPMI.solution.benefit1Desc') }}</p>
                </div>
              </div>
            </div>
            
            <!-- Card 2 -->
            <div class="group bg-gradient-to-br from-purple-50 to-white rounded-2xl p-8 border border-purple-100 hover:shadow-lg hover:shadow-purple-100/50 transition-all duration-300">
              <div class="flex items-start gap-5">
                <div class="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-purple-500/25">
                  <Bot class="h-7 w-7 text-white" />
                </div>
                <div>
                  <h3 class="text-xl font-bold text-gray-900 mb-2">{{ t('offertaPMI.solution.benefit2Title') }}</h3>
                  <p class="text-gray-600 leading-relaxed">{{ t('offertaPMI.solution.benefit2Desc') }} <span class="font-semibold text-purple-600">{{ t('offertaPMI.solution.benefit2Highlight') }}</span></p>
                </div>
              </div>
            </div>
            
            <!-- Card 3 -->
            <div class="group bg-gradient-to-br from-emerald-50 to-white rounded-2xl p-8 border border-emerald-100 hover:shadow-lg hover:shadow-emerald-100/50 transition-all duration-300">
              <div class="flex items-start gap-5">
                <div class="w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-500/25">
                  <Rocket class="h-7 w-7 text-white" />
                </div>
                <div>
                  <h3 class="text-xl font-bold text-gray-900 mb-2">{{ t('offertaPMI.solution.benefit3Title') }}</h3>
                  <p class="text-gray-600 leading-relaxed">{{ t('offertaPMI.solution.benefit3Desc') }}</p>
                </div>
              </div>
            </div>
            
            <!-- Card 4 - Guarantee -->
            <div class="group bg-gradient-to-br from-amber-50 to-white rounded-2xl p-8 border-2 border-amber-200 hover:shadow-lg hover:shadow-amber-100/50 transition-all duration-300">
              <div class="flex items-start gap-5">
                <div class="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-amber-500/25">
                  <Shield class="h-7 w-7 text-white" />
                </div>
                <div>
                  <h3 class="text-xl font-bold text-gray-900 mb-2">{{ t('offertaPMI.solution.benefit4Title') }}</h3>
                  <p class="text-gray-600 leading-relaxed"><span class="font-bold text-amber-600">{{ t('offertaPMI.solution.benefit4Desc') }}</span></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Bonus Section -->
    <section class="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <div class="container mx-auto px-4">
        <div class="max-w-4xl mx-auto">
          <!-- Section Header -->
          <div class="text-center mb-12">
            <div class="inline-flex items-center rounded-full bg-purple-500/20 border border-purple-400/30 text-purple-300 px-4 py-2 text-sm font-semibold mb-4">
              <Gift class="h-4 w-4 mr-2" />
              {{ t('offertaPMI.bonus.badge') }}
            </div>
            <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
              {{ t('offertaPMI.bonus.title') }}
            </h2>
          </div>
          
          <!-- Bonus Cards -->
          <div class="grid md:grid-cols-3 gap-6">
            <div class="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 text-center hover:bg-white/15 transition-all">
              <div class="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                <FileText class="h-7 w-7 text-white" />
              </div>
              <h3 class="text-lg font-bold text-white mb-2">{{ t('offertaPMI.bonus.bonus1Title') }}</h3>
              <p class="text-gray-400 text-sm">{{ t('offertaPMI.bonus.bonus1Desc') }}</p>
            </div>
            
            <div class="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 text-center hover:bg-white/15 transition-all">
              <div class="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Headphones class="h-7 w-7 text-white" />
              </div>
              <h3 class="text-lg font-bold text-white mb-2">{{ t('offertaPMI.bonus.bonus2Title') }}</h3>
              <p class="text-gray-400 text-sm">{{ t('offertaPMI.bonus.bonus2Desc') }}</p>
            </div>
            
            <div class="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 text-center hover:bg-white/15 transition-all">
              <div class="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                <BookOpen class="h-7 w-7 text-white" />
              </div>
              <h3 class="text-lg font-bold text-white mb-2">{{ t('offertaPMI.bonus.bonus3Title') }}</h3>
              <p class="text-gray-400 text-sm">{{ t('offertaPMI.bonus.bonus3Desc') }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Results Section -->
    <section class="py-20 bg-white">
      <div class="container mx-auto px-4">
        <div class="max-w-4xl mx-auto text-center">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-12">
            {{ t('offertaPMI.results.title') }}
          </h2>
          
          <div class="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div class="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-6 border border-blue-100">
              <div class="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Minus class="h-6 w-6 text-blue-600" />
              </div>
              <p class="font-bold text-gray-900">{{ t('offertaPMI.results.result1') }}</p>
            </div>
            
            <div class="bg-gradient-to-br from-purple-50 to-white rounded-2xl p-6 border border-purple-100">
              <div class="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <MailX class="h-6 w-6 text-purple-600" />
              </div>
              <p class="font-bold text-gray-900">{{ t('offertaPMI.results.result2') }}</p>
            </div>
            
            <div class="bg-gradient-to-br from-emerald-50 to-white rounded-2xl p-6 border border-emerald-100">
              <div class="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Users class="h-6 w-6 text-emerald-600" />
              </div>
              <p class="font-bold text-gray-900">{{ t('offertaPMI.results.result3') }}</p>
            </div>
            
            <div class="bg-gradient-to-br from-amber-50 to-white rounded-2xl p-6 border border-amber-100">
              <div class="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Clock class="h-6 w-6 text-amber-600" />
              </div>
              <p class="font-bold text-gray-900">{{ t('offertaPMI.results.result4') }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="py-20 bg-slate-50">
      <div class="container mx-auto px-4">
        <div class="max-w-3xl mx-auto">
          <div class="text-center mb-12">
            <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {{ t('offertaPMI.faq.title') }}
            </h2>
          </div>
          
          <div class="space-y-4">
            <div 
              v-for="(faq, index) in faqs" 
              :key="index"
              class="bg-white rounded-xl border border-gray-200 overflow-hidden"
            >
              <button 
                @click="toggleFaq(index)"
                class="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
              >
                <span class="font-semibold text-gray-900">{{ faq.question }}</span>
                <ChevronDown 
                  class="h-5 w-5 text-gray-500 transition-transform duration-300"
                  :class="{ 'rotate-180': openFaq === index }"
                />
              </button>
              <div 
                v-show="openFaq === index"
                class="px-6 pb-5 text-gray-600"
              >
                {{ faq.answer }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Final CTA Section -->
    <section class="py-20 bg-white">
      <div class="container mx-auto px-4">
        <div class="max-w-4xl mx-auto">
          <div class="bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 rounded-3xl p-10 md:p-16 text-center relative overflow-hidden">
            <!-- Background decoration -->
            <div class="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
            <div class="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl"></div>
            
            <div class="relative z-10">
              <!-- Scarcity Badge -->
              <div class="inline-flex items-center rounded-full bg-white/20 backdrop-blur-sm text-white px-5 py-2 font-semibold text-sm mb-6">
                <Clock class="h-4 w-4 mr-2" />
                {{ t('offertaPMI.finalCta.badge') }}
              </div>
              
              <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
                {{ t('offertaPMI.finalCta.title') }}
              </h2>
              
              <p class="text-lg text-blue-100 mb-8 max-w-2xl mx-auto">
                {{ t('offertaPMI.finalCta.subtitle') }}
              </p>
              
              <!-- CTA Button -->
              <router-link 
                to="/contact" 
                class="inline-flex items-center justify-center h-16 px-10 bg-white text-blue-600 rounded-xl font-bold text-lg transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1"
              >
                {{ t('offertaPMI.finalCta.cta') }}
                <ArrowRight class="ml-3 h-5 w-5" />
              </router-link>
              
              <!-- Trust Badges -->
              <div class="flex flex-wrap justify-center gap-6 mt-8 text-sm text-blue-100">
                <div class="flex items-center">
                  <Check class="h-5 w-5 text-emerald-400 mr-2" />
                  {{ t('offertaPMI.finalCta.trust1') }}
                </div>
                <div class="flex items-center">
                  <Check class="h-5 w-5 text-emerald-400 mr-2" />
                  {{ t('offertaPMI.finalCta.trust2') }}
                </div>
                <div class="flex items-center">
                  <Check class="h-5 w-5 text-emerald-400 mr-2" />
                  {{ t('offertaPMI.finalCta.trust3') }}
                </div>
              </div>
              
              <!-- Logo -->
              <div class="mt-10 text-2xl font-bold text-white/80">
                {{ t('offertaPMI.finalCta.logo') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <FloatingCta />
  </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { 
  Shield, 
  ArrowRight, 
  ChevronDown,
  Search,
  Bot,
  Rocket,
  Gift,
  FileText,
  Headphones,
  BookOpen,
  Minus,
  Mail,
  MailX,
  Users,
  Clock,
  Check,
  Play,
  AlertCircle,
  UserX,
  LayoutDashboard,
  TrendingUp,
  Calculator,
  Zap,
  Lightbulb
} from 'lucide-vue-next'
import FloatingCta from '../components/FloatingCta.vue'

const { t } = useI18n()

// Calculator state
const calc = reactive({
  clients: null,
  hours: null,
  value: null
})

const calcResult = reactive({
  show: false,
  monthlyLoss: 0,
  yearlyLoss: 0,
  hoursLost: 0
})

const calculateLoss = () => {
  if (!calc.clients || !calc.hours || !calc.value) {
    return
  }
  
  // Average hourly rate for a business owner/manager in Ticino (CHF 80-120)
  const hourlyRate = 100
  
  // Calculate monthly hours lost (weekly hours * 4 weeks)
  const monthlyHoursLost = calc.hours * 4
  
  // Calculate opportunity cost (time that could be spent on high-value activities)
  const timeCost = monthlyHoursLost * hourlyRate
  
  // Estimate lost clients due to slow follow-up (typically 20-30% of leads are lost)
  const lostClientPercentage = 0.25
  const potentialLostClients = Math.round(calc.clients * lostClientPercentage)
  const lostRevenue = potentialLostClients * calc.value
  
  // Total monthly loss
  const monthlyLoss = timeCost + lostRevenue
  
  calcResult.monthlyLoss = Math.round(monthlyLoss)
  calcResult.yearlyLoss = Math.round(monthlyLoss * 12)
  calcResult.hoursLost = monthlyHoursLost
  calcResult.show = true
}

const openFaq = ref(null)

const faqs = computed(() => [
  {
    question: t('offertaPMI.faq.q1'),
    answer: t('offertaPMI.faq.a1')
  },
  {
    question: t('offertaPMI.faq.q2'),
    answer: t('offertaPMI.faq.a2')
  },
  {
    question: t('offertaPMI.faq.q3'),
    answer: t('offertaPMI.faq.a3')
  },
  {
    question: t('offertaPMI.faq.q4'),
    answer: t('offertaPMI.faq.a4')
  }
])

const toggleFaq = (index) => {
  openFaq.value = openFaq.value === index ? null : index
}

const scrollToHow = () => {
  const section = document.getElementById('how-it-works')
  if (section) {
    section.scrollIntoView({ behavior: 'smooth' })
  }
}

defineOptions({
  name: 'OffertaPMIPage'
})
</script>

<style scoped>
.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
</style>
